import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { productAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CATS = ['Formal','Party','Traditional','Casual','Streetwear','Luxury Evening','Ethnic','Western','Fusion'];

const CAT_COLOR = {
  'Formal':'#1565c0','Party':'#7b1fa2','Traditional':'#c62828','Casual':'#2e7d32',
  'Streetwear':'#e65100','Luxury Evening':'#4a148c','Ethnic':'#bf360c','Western':'#1a237e','Fusion':'#880e4f',
};

export default function HomePage() {
  const navigate         = useNavigate();
  const { user }         = useAuth();
  const [popular,  setPopular]  = useState([]);
  const [latest,   setLatest]   = useState([]);
  const [loaded,   setLoaded]   = useState(false);
  const [heroReady,setHeroReady]= useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => setHeroReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  // Cycle hero image every 3s once products are loaded
  useEffect(() => {
    if (!loaded) return;
    const allProducts = [...popular, ...latest].filter(p => p?.images?.length > 0);
    if (allProducts.length < 2) return;
    const iv = setInterval(() => setActiveImg(i => (i + 1) % Math.min(allProducts.length, 8)), 3000);
    return () => clearInterval(iv);
  }, [loaded, popular, latest]); // eslint-disable-line

  useEffect(() => {
    Promise.all([
      productAPI.getAll({ limit: 16, sort: 'popular' }),
      productAPI.getAll({ limit: 16, sort: '-createdAt' }),
    ]).then(([p, l]) => {
      setPopular(p.data.products);
      setLatest(l.data.products);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  return (
    <div style={{ background: 'var(--cream)', minHeight: '100vh' }}>

      {/* ══ HERO ══════════════════════════════════════════════════════════════ */}
      <section style={{
        position: 'relative', overflow: 'hidden',
        background: 'var(--cream-dark)',
        borderBottom: '1px solid var(--border)',
      }}
      className="hero-section">
        <style>{`
          .hero-grid { display: grid; grid-template-columns: 1fr 1fr; align-items: stretch; min-height: clamp(380px, 62vh, 560px); }
          .hero-left  { display: flex; flex-direction: column; justify-content: center; padding: clamp(36px,5vw,64px) clamp(28px,5vw,72px); }
          .hero-right { position: relative; overflow: hidden; min-height: clamp(380px, 62vh, 560px); }
          .hero-right img { width:100%; height:100%; object-fit:cover; object-position:top; display:block; }
          .mobile-product-teaser { display: none !important; }
          .hiw-mobile { display: none !important; }
          .hiw-grid   { display: grid !important; }
          @media (max-width: 900px) {
            .hero-grid { grid-template-columns: 1fr !important; min-height: auto !important; }
            .hero-right { min-height: 60vw !important; max-height: 480px; }
            .hero-left  { padding: 36px 24px 28px !important; }
            .hiw-grid   { display: none !important; }
            .hiw-mobile { display: block !important; }
          }
        `}</style>

        {/* Gold top border */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, transparent, var(--gold) 25%, var(--gold-light) 50%, var(--gold) 75%, transparent)', zIndex: 2 }} />

        <div style={{
          position: 'relative', zIndex: 1,
          opacity: heroReady ? 1 : 0,
          transform: heroReady ? 'none' : 'translateY(12px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}>
          <div className="hero-grid" style={{ maxWidth: '100%' }}>

            {/* ── Left: text content ── */}
            <div className="hero-left">
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.32em', color: 'var(--gold)', textTransform: 'uppercase', marginBottom: 14 }}>
                Warbadobe Rentals · Premium Clothing Rental
              </div>
              <h1 style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: 'clamp(32px, 6vw, 72px)',
                fontWeight: 300, fontStyle: 'italic',
                color: 'var(--ink)', lineHeight: 1.08,
                margin: '0 0 clamp(10px,2vw,18px)',
              }}>
                Own the Night.{' '}
                <span style={{ color: 'var(--gold)' }}>Not the Outfit.</span>
              </h1>
              <p style={{
                fontSize: 'clamp(12px,1.4vw,14px)',
                color: 'var(--muted)',
                lineHeight: 1.75, maxWidth: 460,
                margin: '0 0 clamp(18px,3vw,28px)',
              }}>
                Premium outfits for every occasion — rented, delivered, and fully refundable.
              </p>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button onClick={() => navigate('/browse')}
                  style={{ background: 'var(--ink)', color: 'var(--cream)', border: 'none', padding: 'clamp(11px,1.6vw,13px) clamp(22px,3vw,32px)', fontWeight: 700, fontSize: 10, cursor: 'pointer', letterSpacing: '0.18em', textTransform: 'uppercase', transition: 'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background='var(--gold)'}
                  onMouseLeave={e => e.currentTarget.style.background='var(--ink)'}>
                  Browse Collection
                </button>
                {!user && (
                  <button onClick={() => navigate('/register')}
                    style={{ background: 'none', color: 'var(--ink)', border: '1px solid var(--border-strong)', padding: 'clamp(11px,1.6vw,13px) clamp(22px,3vw,32px)', fontWeight: 600, fontSize: 10, cursor: 'pointer', letterSpacing: '0.14em', textTransform: 'uppercase', transition: 'all 0.2s' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.color='var(--gold)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-strong)'; e.currentTarget.style.color='var(--ink)'; }}>
                    Join Free
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 'clamp(16px,4vw,40px)', marginTop: 'clamp(16px,2.5vw,28px)', flexWrap: 'wrap', paddingTop: 'clamp(14px,2vw,22px)', borderTop: '1px solid var(--border)' }}>
                {[
                  ['100%', 'Deposit Refunded'],
                  ['₹299', 'Rentals From'],
                  ['48hr', 'Delivery'],
                ].map(([val, lbl]) => (
                  <div key={lbl}>
                    <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(17px,2.2vw,22px)', color: 'var(--ink)', fontWeight: 400, lineHeight: 1 }}>{val}</div>
                    <div style={{ fontSize: 9, color: 'var(--muted)', letterSpacing: '0.16em', textTransform: 'uppercase', marginTop: 3 }}>{lbl}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* ── Right: cycling product image ── */}
            <div className="hero-right">
              <style>{`
                @keyframes heroImgIn  { from{opacity:0;transform:scale(1.04)} to{opacity:1;transform:scale(1)} }
                @keyframes heroImgOut { from{opacity:1;transform:scale(1)}    to{opacity:0;transform:scale(0.97)} }
                .hero-img-active { animation: heroImgIn 0.7s ease both; }
              `}</style>

              {(() => {
                const allProducts = [...popular, ...latest].filter(p => p?.images?.length > 0);
                const product = allProducts[activeImg % Math.max(allProducts.length, 1)];
                const img = product?.images?.find(i => i.isPrimary)?.url || product?.images?.[0]?.url;
                if (!img) return (
                  <div style={{ width:'100%', height:'100%', background:'var(--cream-dark)', display:'flex', alignItems:'center', justifyContent:'center' }}>
                    <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:72, color:'var(--border)', fontStyle:'italic' }}>W</span>
                  </div>
                );
                return (
                  <div key={activeImg} className="hero-img-active"
                    onClick={() => product && navigate('/product/' + product._id)}
                    style={{ width:'100%', height:'100%', cursor: product ? 'pointer' : 'default', position:'relative' }}>
                    <img src={img} alt={product?.name || ''} style={{ width:'100%', height:'100%', objectFit:'cover', objectPosition:'top', display:'block' }} />
                    {/* Overlay info */}
                    <div style={{ position:'absolute', bottom:0, left:0, right:0, padding:'32px 20px 20px', background:'linear-gradient(transparent, rgba(14,11,8,0.55))' }}>
                      <div style={{ fontSize:9, fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', color:'rgba(255,255,255,0.7)', marginBottom:4 }}>{product?.category}</div>
                      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:'white', fontStyle:'italic', marginBottom:6, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{product?.name}</div>
                      <div style={{ fontSize:13, color:'var(--gold)', fontWeight:700 }}>from ₹{product?.rentalPrices?.twoDays?.toLocaleString('en-IN')}</div>
                    </div>
                  </div>
                );
              })()}

              {/* Dot indicators */}
              {(() => {
                const total = Math.min([...popular,...latest].filter(p=>p?.images?.length>0).length, 8);
                if (total < 2) return null;
                return (
                  <div style={{ position:'absolute', bottom:16, right:16, display:'flex', gap:5, zIndex:2 }}>
                    {Array.from({length: total}).map((_,i) => (
                      <button key={i} onClick={() => setActiveImg(i)}
                        style={{ width: i===activeImg ? 20 : 6, height:6, borderRadius:3, border:'none', cursor:'pointer', padding:0, transition:'all 0.3s', background: i===activeImg ? 'var(--gold)' : 'rgba(255,255,255,0.5)' }} />
                    ))}
                  </div>
                );
              })()}
            </div>

          </div>
        </div>
      </section>

      {/* ══ MOBILE PRODUCT TEASER (hidden on desktop) ════════════════════════ */}
      <div className="mobile-product-teaser">
        {[...(popular || []), ...(latest || [])].filter(p => p?.images?.length).slice(0, 5).map((p, i) => {
          const imgUrl = p.images.find(img => img.isPrimary)?.url || p.images[0]?.url;
          const price  = p.minPrice || p.rentalPrices?.twoDays || p.rentalPrices?.fiveDays || 0;
          if (!imgUrl) return null;
          return (
            <button key={i} onClick={() => navigate(`/product/${p._id || p.id}`)}
              style={{ flex: '0 0 42vw', border: 'none', background: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}>
              <div style={{ aspectRatio: '3/4', overflow: 'hidden', background: 'var(--cream)', position: 'relative' }}>
                <img src={imgUrl} alt={p.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }} />
                <div style={{ position: 'absolute', bottom: 6, left: 6, fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', background: 'rgba(14,11,8,0.55)', padding: '3px 7px' }}>
                  {p.category}
                </div>
              </div>
              <div style={{ padding: '8px 2px 0' }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                <div style={{ fontSize: 11, color: 'var(--gold)', marginTop: 2 }}>
                  {price ? <>₹{price.toLocaleString('en-IN')}<span style={{ color: 'var(--muted)', fontSize: 9 }}>/rental</span></> : ''}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ══ CATEGORIES ════════════════════════════════════════════════════════ */}
      <section style={{ background: '#fff', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(24px,4vw,40px) clamp(20px,4vw,48px)' }}>
          <SectionLabel>Browse by Category</SectionLabel>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(80px,9vw,110px), 1fr))', gap: 10, marginTop: 20 }}>
            {CATS.map(cat => (
              <button key={cat} onClick={() => navigate(`/browse?category=${cat}`)}
                style={{ padding: 'clamp(12px,2vw,18px) 8px', background: 'var(--cream)', border: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.2s', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
                onMouseEnter={e => { e.currentTarget.style.background='var(--gold-pale)'; e.currentTarget.style.borderColor='var(--gold)'; }}
                onMouseLeave={e => { e.currentTarget.style.background='var(--cream)'; e.currentTarget.style.borderColor='var(--border)'; }}>
                <div style={{ width: 32, height: 32, borderRadius: '50%', background: CAT_COLOR[cat] + '18', border: `1px solid ${CAT_COLOR[cat]}33`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: CAT_COLOR[cat] }} />
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink)', lineHeight: 1.3 }}>{cat}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ══ POPULAR ═══════════════════════════════════════════════════════════ */}
      <section style={{ background: 'var(--cream)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(28px,5vw,52px) clamp(20px,4vw,48px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'clamp(16px,2.5vw,28px)' }}>
            <div>
              <SectionLabel>Most Rented</SectionLabel>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(22px,3.5vw,36px)', fontStyle: 'italic', fontWeight: 300, color: 'var(--ink)', margin: '4px 0 0' }}>
                What Everyone's Wearing
              </h2>
            </div>
            <Link to="/browse?sort=popular" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', textDecoration: 'none', borderBottom: '1px solid var(--gold)', paddingBottom: 2, flexShrink: 0 }}>
              See All
            </Link>
          </div>
          {loaded ? <ProductGrid products={popular} navigate={navigate} /> : <GridSkeleton />}
        </div>
      </section>

      {/* ══ HOW IT WORKS — editorial strip ═══════════════════════════════════ */}
      <section style={{ background: 'var(--cream-dark)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(32px,5vw,52px) clamp(20px,4vw,48px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'clamp(20px,3vw,32px)', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <SectionLabel>The Process</SectionLabel>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(20px,3vw,32px)', fontWeight: 300, fontStyle: 'italic', color: 'var(--ink)', margin: '4px 0 0' }}>
                Four steps to looking extraordinary
              </h2>
            </div>
          </div>
          {/* Desktop: 4-column grid */}
          <div className="hiw-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 0, border: '1px solid var(--border)' }}>
            {[
              { n: '01', title: 'Browse & Choose', desc: 'Filter by category, size and occasion. Every piece is real, photographed, and available.', icon: (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="8" y="6" width="22" height="28" rx="2"/>
                  <path d="M16 6V4M24 6V4"/>
                  <path d="M12 16h14M12 21h10M12 26h7"/>
                  <circle cx="34" cy="34" r="8"/>
                  <path d="M40 40l4 4"/>
                </svg>
              )},
              { n: '02', title: 'Book Your Dates', desc: 'Pick rental duration and delivery date. Pay securely online or choose cash on delivery.', icon: (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="10" width="36" height="32" rx="2"/>
                  <path d="M6 18h36"/>
                  <path d="M16 6v8M32 6v8"/>
                  <path d="M14 28h6v6h-6z"/>
                </svg>
              )},
              { n: '03', title: 'Wear & Shine', desc: 'Delivered to your door, dry-cleaned and pressed. Show up looking extraordinary.', icon: (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 6l-8 10h6v22h16V16h6L30 6"/>
                  <path d="M18 6h12"/>
                  <path d="M24 6v6"/>
                  <path d="M20 24l3 3 6-6"/>
                </svg>
              )},
              { n: '04', title: 'Return & Refund', desc: 'Drop it back. Your full security deposit is returned within 3–5 days, no questions asked.', icon: (
                <svg width="28" height="28" viewBox="0 0 48 48" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 20l8-10 8 10"/>
                  <path d="M16 10v18a8 8 0 008 8h8"/>
                  <path d="M36 32l4 4-4 4"/>
                  <circle cx="36" cy="20" r="6"/>
                  <path d="M34 20l1.5 1.5L39 17"/>
                </svg>
              )},
            ].map((s, i) => (
              <div key={s.n} style={{
                padding: 'clamp(18px,2.5vw,28px)',
                background: '#fff',
                borderRight: i < 3 ? '1px solid var(--border)' : 'none',
                position: 'relative', overflow: 'hidden',
                transition: 'background 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.background='var(--cream)'}
                onMouseLeave={e => e.currentTarget.style.background='#fff'}
              >
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 72, fontWeight: 700, color: 'rgba(184,146,74,0.05)', lineHeight: 1, position: 'absolute', bottom: -6, right: 6, userSelect: 'none', pointerEvents: 'none' }}>{s.n}</div>
                <div style={{ width: 48, height: 48, background: 'var(--cream-dark)', border: '1px solid rgba(184,146,74,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, color: 'var(--gold)' }}>{s.icon}</div>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 6 }}>Step {s.n}</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(15px,1.8vw,19px)', color: 'var(--ink)', fontWeight: 400, marginBottom: 8, lineHeight: 1.2 }}>{s.title}</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.8 }}>{s.desc}</div>
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: 'linear-gradient(90deg, var(--gold), transparent)' }} />
              </div>
            ))}
          </div>

          {/* Mobile: compact horizontal scroll timeline */}
          <div className="hiw-mobile">
            {[
              { n: '01', title: 'Browse & Choose', desc: 'Filter by size, category & occasion.' },
              { n: '02', title: 'Book Your Dates', desc: 'Pick dates, pay online or cash on delivery.' },
              { n: '03', title: 'Wear & Shine', desc: 'Delivered clean, pressed, ready to wear.' },
              { n: '04', title: 'Return & Refund', desc: 'Return it. Full deposit back in 3–5 days.' },
            ].map((s, i) => (
              <div key={s.n} style={{ display: 'flex', gap: 14, alignItems: 'flex-start', padding: '16px 0', borderBottom: i < 3 ? '1px solid var(--border)' : 'none' }}>
                {/* Number bubble */}
                <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: 'var(--ink)', color: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Cormorant Garamond',serif", fontSize: 14, fontWeight: 700, marginTop: 2 }}>{s.n}</div>
                {/* Connector line */}
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 17, color: 'var(--ink)', fontWeight: 400, marginBottom: 3 }}>{s.title}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.6 }}>{s.desc}</div>
                </div>
                <div style={{ flexShrink: 0, fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', color: 'var(--gold)', paddingTop: 4 }}>0{i+1}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ NEW ARRIVALS ══════════════════════════════════════════════════════ */}
      <section style={{ background: '#fff' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(28px,5vw,52px) clamp(20px,4vw,48px)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 'clamp(16px,2.5vw,28px)' }}>
            <div>
              <SectionLabel>Just Landed</SectionLabel>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(22px,3.5vw,36px)', fontStyle: 'italic', fontWeight: 300, color: 'var(--ink)', margin: '4px 0 0' }}>
                New Arrivals
              </h2>
            </div>
            <Link to="/browse?sort=-createdAt" style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--gold)', textDecoration: 'none', borderBottom: '1px solid var(--gold)', paddingBottom: 2, flexShrink: 0 }}>
              See All
            </Link>
          </div>
          {loaded ? <ProductGrid products={latest} navigate={navigate} isNew /> : <GridSkeleton />}
        </div>
      </section>

      {/* ══ WHY RENT — value props ════════════════════════════════════════════ */}
      <section style={{ background: 'var(--cream-dark)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: 'clamp(36px,5vw,56px) clamp(20px,4vw,48px)' }}>
          <div style={{ textAlign: 'center', marginBottom: 'clamp(24px,4vw,40px)' }}>
            <SectionLabel>Why Warbadobe</SectionLabel>
            <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(22px,3.5vw,40px)', fontWeight: 300, fontStyle: 'italic', color: 'var(--ink)', margin: '6px 0 0' }}>
              Wear more. Spend less. Waste nothing.
            </h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px,1fr))', gap: 12 }}>
            {[
              { title: 'Full Refund Guarantee', desc: 'Your security deposit comes back in full within 3–5 days after safe return. No questions asked.' },
              { title: 'Professionally Cleaned', desc: 'Every outfit is dry-cleaned and pressed before delivery. You receive it spotless, ready to wear.' },
              { title: 'No Late Fine', desc: 'Life happens. We don\'t charge late fees — just return when you can and we\'ll sort it out.' },
              { title: 'Free Delivery on ₹999+', desc: 'Order above ₹999 and we deliver and pick up for free. Otherwise a small logistics fee applies.' },
            ].map(v => (
              <div key={v.title} style={{ padding: 'clamp(18px,2.5vw,28px)', background: '#fff', border: '1px solid var(--border)', borderTop: '2px solid var(--gold)' }}>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(16px,2vw,20px)', color: 'var(--ink)', marginBottom: 10, fontWeight: 400 }}>{v.title}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.8 }}>{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ═════════════════════════════════════════════════════════ */}
      {!user && (
        <section style={{ background: 'var(--cream)', textAlign: 'center', padding: 'clamp(48px,8vw,80px) clamp(20px,5vw,48px)' }}>
          <div style={{ fontSize: 9, letterSpacing: '0.3em', color: 'var(--gold)', fontWeight: 700, textTransform: 'uppercase', marginBottom: 16 }}>
            Ready to Start?
          </div>
          <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 'clamp(28px,5vw,56px)', fontWeight: 300, fontStyle: 'italic', color: 'var(--ink)', margin: '0 0 12px', lineHeight: 1.1 }}>
            Your next outfit is<br />one click away
          </h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 32, maxWidth: 400, margin: '0 auto 32px', lineHeight: 1.8 }}>
            Create a free account. Browse hundreds of premium outfits. Rent the one you love.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/register')}
              style={{ background: 'var(--ink)', color: 'var(--cream)', border: 'none', padding: '13px 36px', fontWeight: 700, fontSize: 10, cursor: 'pointer', letterSpacing: '0.18em', textTransform: 'uppercase', transition: 'background 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.background='var(--gold)'}
              onMouseLeave={e => e.currentTarget.style.background='var(--ink)'}>
              Create Free Account
            </button>
            <button onClick={() => navigate('/browse')}
              style={{ background: 'none', color: 'var(--ink)', border: '1px solid var(--border-strong)', padding: '13px 36px', fontWeight: 600, fontSize: 10, cursor: 'pointer', letterSpacing: '0.14em', textTransform: 'uppercase', transition: 'all 0.2s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor='var(--gold)'; e.currentTarget.style.color='var(--gold)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor='var(--border-strong)'; e.currentTarget.style.color='var(--ink)'; }}>
              Browse First
            </button>
          </div>
        </section>
      )}
    </div>
  );
}

// ── Product Grid ──────────────────────────────────────────────────────────────
function ProductGrid({ products, navigate, isNew }) {
  if (!products.length) return (
    <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)', fontSize: 13 }}>No products yet.</div>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(140px,16vw,200px), 1fr))', gap: 'clamp(10px,1.5vw,16px)' }}>
      {products.map(p => <ProductCard key={p._id} product={p} navigate={navigate} isNew={isNew} />)}
    </div>
  );
}

function ProductCard({ product, navigate, isNew }) {
  const [hov, setHov] = useState(false);
  const price      = product.rentalPrices?.twoDays || product.rentalPrices?.threeDays || Object.values(product.rentalPrices || {})[0] || 0;
  const catColor   = CAT_COLOR[product.category] || 'var(--gold)';
  const primaryImg = product.images?.find(i => i.isPrimary)?.url || product.images?.[0]?.url;

  return (
    <div onClick={() => navigate(`/product/${product._id}`)}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ cursor: 'pointer', background: '#fff', border: `1px solid ${hov ? 'var(--gold)' : 'var(--border)'}`, transition: 'all 0.22s', transform: hov ? 'translateY(-5px)' : 'none', boxShadow: hov ? '0 12px 32px rgba(14,11,8,0.12)' : 'none' }}>

      {/* Image */}
      <div style={{ aspectRatio: '3/4', background: 'var(--cream-dark)', position: 'relative', overflow: 'hidden' }}>
        {primaryImg
          ? <img src={primaryImg} alt={product.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transition: 'transform 0.5s ease', transform: hov ? 'scale(1.07)' : 'scale(1)' }} />
          : <div style={{ height: '100%', background: `linear-gradient(135deg,${catColor}22,${catColor}08)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 48, color: catColor, opacity: 0.2, fontStyle: 'italic' }}>W</div>
            </div>
        }
        {isNew && <span style={{ position: 'absolute', top: 10, left: 10, background: 'var(--gold)', color: 'var(--ink)', fontSize: 8, fontWeight: 800, padding: '3px 8px', letterSpacing: '0.14em', textTransform: 'uppercase' }}>New</span>}
        {!product.isActive && (
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(14,11,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ fontSize: 9, fontWeight: 800, color: '#fff', letterSpacing: '0.22em', textTransform: 'uppercase', border: '1px solid rgba(255,255,255,0.4)', padding: '4px 10px' }}>Rented</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: 'clamp(10px,1.5vw,14px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: catColor, flexShrink: 0 }} />
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: catColor }}>{product.category}</span>
        </div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontStyle: 'italic', fontSize: 'clamp(13px,1.5vw,15px)', color: 'var(--ink)', lineHeight: 1.3, marginBottom: 8, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {product.name}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginBottom: 7 }}>
          <span style={{ fontSize: 'clamp(13px,1.6vw,15px)', fontWeight: 700, color: 'var(--ink)' }}>₹{price?.toLocaleString('en-IN')}</span>
          <span style={{ fontSize: 10, color: 'var(--muted)' }}>/rental</span>
        </div>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {product.sizes?.slice(0,5).map(sz => (
            <span key={sz} style={{ fontSize: 9, border: '1px solid var(--border)', padding: '1px 5px', color: 'var(--muted)' }}>{sz}</span>
          ))}
          {product.sizes?.length > 5 && <span style={{ fontSize: 9, color: 'var(--muted)' }}>+{product.sizes.length - 5}</span>}
        </div>
      </div>
    </div>
  );
}

// ── Hero Mosaic Tile ─────────────────────────────────────────────────────────
function HeroMosaicTile({ item, products, index }) {
  // Pick a product from popular list matching the category, or use placeholder
  const match = products.find(p => p.category === item.cat);
  const imgUrl = match?.images?.find(i => i.isPrimary)?.url || match?.images?.[0]?.url;

  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      background: item.bg + '22',
    }}>
      {imgUrl ? (
        <img
          src={imgUrl}
          alt={item.cat}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div style={{
          height: '100%', minHeight: 120,
          background: `linear-gradient(135deg, ${item.bg}18, ${item.bg}08)`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          <span style={{ fontSize: 28 }}>{item.emoji}</span>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: item.bg, opacity: 0.6 }}>{item.cat}</span>
        </div>
      )}
      {/* Category label overlay */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(transparent, rgba(14,11,8,0.55))',
        padding: '16px 10px 8px',
      }}>
        <div style={{ fontSize: 8, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}>
          {item.cat}
        </div>
      </div>
    </div>
  );
}

// ── Grid Skeleton ─────────────────────────────────────────────────────────────
function GridSkeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(140px,16vw,200px), 1fr))', gap: 'clamp(10px,1.5vw,16px)' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} style={{ background: '#fff', border: '1px solid var(--border)' }}>
          <div style={{ aspectRatio: '3/4', background: 'var(--cream-dark)', animation: 'pulse 1.4s ease-in-out infinite', animationDelay: `${i * 0.07}s` }} />
          <div style={{ padding: 12 }}>
            <div style={{ height: 8, background: 'var(--cream-dark)', marginBottom: 8, width: '50%', animation: 'pulse 1.4s ease-in-out infinite' }} />
            <div style={{ height: 12, background: 'var(--cream-dark)', marginBottom: 8, animation: 'pulse 1.4s ease-in-out infinite' }} />
            <div style={{ height: 10, background: 'var(--cream-dark)', width: '40%', animation: 'pulse 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Section Label ─────────────────────────────────────────────────────────────
function SectionLabel({ children, light }) {
  return (
    <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.28em', textTransform: 'uppercase', color: light ? 'rgba(184,146,74,0.7)' : 'var(--gold)' }}>
      {children}
    </div>
  );
}